/** Org Chart — classic zoomable/draggable tree, tabular listing, and AI-suggested reporting lines. */
import { createElement, useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { PersonChip } from "@/components/PersonChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Network, ZoomIn, ZoomOut, Maximize2, Sparkles, ChevronDown, ChevronRight, Users, Check, Loader2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const ALL = "__all__";
const NONE = "__none__";

export default function OrgChart() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can } = useWorkspacePerms();
  const canManage = can("employee.manage") || can("hr.manage");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [dept, setDept] = useState(ALL);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      const { data: d } = await api.get(`/workspaces/${wsId}/org-chart`);
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load org chart")); }
    finally { setBusy(false); }
  }, [wsId]);
  useEffect(() => { load(); }, [load]);

  const emps = data?.employees || [];
  const filtered = useMemo(() => emps.filter((e) =>
    (dept === ALL || e.department === dept) &&
    (!q || (e.full_name || "").toLowerCase().includes(q.toLowerCase()) || (e.designation || "").toLowerCase().includes(q.toLowerCase()))
  ), [emps, dept, q]);

  const assign = async (employeeId, managerUserId) => {
    try {
      await api.patch(`/workspaces/${wsId}/org-chart/assign`, { employee_id: employeeId, manager_user_id: managerUserId });
      toast.success("Reporting line updated");
      load();
      return true;
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to update")); return false; }
  };

  return (
    <>
      <PageHeader eyebrow="HR & Org" title="Org Chart"
        description="Who reports to whom — drag to restructure, or let AI suggest reporting lines." />
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="orgchart-toolbar">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or designation…"
          className="w-56 rounded-none" data-testid="orgchart-search" />
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-48 rounded-none" data-testid="orgchart-dept-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All departments</SelectItem>
            {(data?.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        {data?.stats && (
          <div className="ml-auto flex gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="rounded-none">{data.stats.total} people</Badge>
            <Badge variant="outline" className="rounded-none">{data.stats.managers} managers</Badge>
            <Badge variant="outline" className="rounded-none">{data.stats.total - data.stats.with_manager} unassigned</Badge>
          </div>
        )}
      </div>
      <Tabs defaultValue="tree">
        <TabsList className="rounded-none">
          <TabsTrigger value="tree" className="rounded-none" data-testid="orgchart-tab-tree"><Network className="w-4 h-4 mr-1.5" />Tree</TabsTrigger>
          <TabsTrigger value="table" className="rounded-none" data-testid="orgchart-tab-table"><Users className="w-4 h-4 mr-1.5" />Table</TabsTrigger>
          <TabsTrigger value="ai" className="rounded-none" data-testid="orgchart-tab-ai"><Sparkles className="w-4 h-4 mr-1.5" />AI Suggestions</TabsTrigger>
        </TabsList>
        <TabsContent value="tree">
          {busy ? <p className="text-sm text-muted-foreground p-8">Loading…</p> :
            <TreeView emps={emps} filteredIds={new Set(filtered.map((e) => e.id))} canManage={canManage} onAssign={assign} highlight={q} />}
        </TabsContent>
        <TabsContent value="table">
          <TableView emps={filtered} allEmps={emps} canManage={canManage} onAssign={assign} />
        </TabsContent>
        <TabsContent value="ai">
          <AiSuggestions wsId={wsId} canManage={canManage} onAssign={assign} />
        </TabsContent>
      </Tabs>
    </>
  );
}

/* ───────── Tree view: zoom + pan + drag-reassign + collapse ───────── */
function TreeView({ emps, filteredIds, canManage, onAssign, highlight }) {
  const [zoom, setZoom] = useState(0.85);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [dragId, setDragId] = useState(null);
  const panRef = useRef(null);

  const { roots, childrenOf, unassigned } = useMemo(() => {
    const byUid = {};
    emps.forEach((e) => { if (e.user_id) byUid[e.user_id] = e; });
    const kids = {};
    emps.forEach((e) => {
      const mgr = byUid[e.reporting_manager_id];
      if (mgr && mgr.id !== e.id) (kids[mgr.id] = kids[mgr.id] || []).push(e);
    });
    const hasMgr = new Set(Object.values(kids).flat().map((e) => e.id));
    const rootsAll = emps.filter((e) => !hasMgr.has(e.id));
    return {
      roots: rootsAll.filter((e) => (kids[e.id] || []).length > 0),
      childrenOf: kids,
      unassigned: rootsAll.filter((e) => !(kids[e.id] || []).length),
    };
  }, [emps]);

  const toggle = (id) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const onMouseDown = (e) => {
    if (e.target.closest("[data-orgnode]")) return;
    const start = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    const move = (ev) => setPan({ x: ev.clientX - start.x, y: ev.clientY - start.y });
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  const handleDrop = async (targetEmp) => {
    if (!dragId || dragId === targetEmp.id) return;
    if (!targetEmp.user_id) { toast.error(`${targetEmp.full_name} has no linked user account — cannot be a manager`); return; }
    const dragged = emps.find((e) => e.id === dragId);
    if (dragged) await onAssign(dragged.id, targetEmp.user_id);
    setDragId(null);
  };

  return (
    <div className="border bg-muted/20 relative overflow-hidden" style={{ height: "calc(100vh - 320px)", minHeight: 480 }} data-testid="orgchart-tree-canvas">
      <div className="absolute top-3 left-3 z-10 flex gap-1">
        <Button size="icon" variant="outline" className="rounded-none h-8 w-8 bg-background" onClick={() => setZoom((z) => Math.min(2, z + 0.15))} data-testid="orgchart-zoom-in"><ZoomIn className="w-4 h-4" /></Button>
        <Button size="icon" variant="outline" className="rounded-none h-8 w-8 bg-background" onClick={() => setZoom((z) => Math.max(0.3, z - 0.15))} data-testid="orgchart-zoom-out"><ZoomOut className="w-4 h-4" /></Button>
        <Button size="icon" variant="outline" className="rounded-none h-8 w-8 bg-background" onClick={() => { setZoom(0.85); setPan({ x: 0, y: 0 }); }} data-testid="orgchart-zoom-reset"><Maximize2 className="w-4 h-4" /></Button>
        <span className="text-xs text-muted-foreground self-center ml-2 bg-background/80 px-2 py-1">{Math.round(zoom * 100)}% · drag canvas to pan{canManage ? " · drag cards to re-assign" : ""}</span>
      </div>
      <div ref={panRef} className="w-full h-full cursor-grab active:cursor-grabbing overflow-auto" onMouseDown={onMouseDown}>
        <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: "top center", transition: dragId ? "none" : "transform 120ms ease-out" }}
          className="pt-16 pb-10 px-8 min-w-max mx-auto">
          {roots.length === 0 && <p className="text-sm text-muted-foreground text-center">No reporting lines yet — use the Table or AI tab to assign managers.</p>}
          <div className="flex items-start justify-center gap-10">
            {roots.map((r) => (
              <OrgNode key={r.id} emp={r} childrenOf={childrenOf} collapsed={collapsed} toggle={toggle}
                canManage={canManage} dragId={dragId} setDragId={setDragId} onDrop={handleDrop}
                filteredIds={filteredIds} highlight={highlight} />
            ))}
          </div>
          {unassigned.length > 0 && (
            <div className="mt-12 max-w-4xl mx-auto" data-testid="orgchart-unassigned">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Unassigned ({unassigned.length}) — drag onto a card to place in the tree</p>
              <div className="flex flex-wrap gap-2">
                {unassigned.filter((e) => filteredIds.has(e.id)).slice(0, 60).map((e) => (
                  <NodeCard key={e.id} emp={e} small canManage={canManage} dragId={dragId} setDragId={setDragId} onDrop={handleDrop} highlight={highlight} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OrgNode({ emp, childrenOf, collapsed, toggle, canManage, dragId, setDragId, onDrop, filteredIds, highlight }) {
  const kids = childrenOf[emp.id] || [];
  const isCollapsed = collapsed.has(emp.id);
  return (
    <div className="flex flex-col items-center">
      <NodeCard emp={emp} canManage={canManage} dragId={dragId} setDragId={setDragId} onDrop={onDrop}
        kidCount={kids.length} isCollapsed={isCollapsed} onToggle={() => toggle(emp.id)} highlight={highlight} dim={!filteredIds.has(emp.id)} />
      {kids.length > 0 && !isCollapsed && (
        <>
          <div className="w-px h-5 bg-border" />
          <div className="flex items-start gap-6 relative pt-5">
            <div className="absolute top-0 left-0 right-0 flex justify-between px-[calc(50%/var(--n))]" />
            <div className="absolute top-0 h-px bg-border" style={{ left: "calc(0% + 3rem)", right: "calc(0% + 3rem)" }} />
            {kids.map((k) => (
              <div key={k.id} className="flex flex-col items-center relative">
                <div className="absolute -top-5 w-px h-5 bg-border" />
                {/* createElement avoids visual-edits babel plugin recursion crash */}
                {createElement(OrgNode, { emp: k, childrenOf, collapsed, toggle, canManage, dragId, setDragId, onDrop, filteredIds, highlight })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NodeCard({ emp, small, canManage, dragId, setDragId, onDrop, kidCount = 0, isCollapsed, onToggle, highlight, dim }) {
  const [over, setOver] = useState(false);
  const match = highlight && (emp.full_name || "").toLowerCase().includes(highlight.toLowerCase());
  return (
    <div data-orgnode data-testid={`orgnode-${emp.id}`}
      draggable={canManage}
      onDragStart={(e) => { setDragId(emp.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={() => setDragId(null)}
      onDragOver={(e) => { if (dragId && dragId !== emp.id) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop(emp); }}
      className={`bg-background border px-3 py-2 flex items-center gap-2 select-none transition-colors
        ${small ? "w-52" : "w-60"} ${over ? "border-primary ring-2 ring-primary/40" : ""}
        ${match ? "border-amber-500 ring-1 ring-amber-400/50" : ""} ${dim && !match ? "opacity-40" : ""}
        ${canManage ? "cursor-grab active:cursor-grabbing" : ""}`}>
      {canManage && <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />}
      <Avatar className="w-8 h-8 rounded-none shrink-0">
        {emp.avatar_data_url && <AvatarImage src={emp.avatar_data_url} />}
        <AvatarFallback className="rounded-none text-xs">{(emp.full_name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        {emp.user_id
          ? <PersonChip userId={emp.user_id} name={emp.full_name} className="text-sm font-semibold truncate block no-underline decoration-transparent" />
          : <p className="text-sm font-semibold truncate">{emp.full_name}</p>}
        <p className="text-[11px] text-muted-foreground truncate">{emp.designation || emp.department || "—"}</p>
      </div>
      {kidCount > 0 && (
        <button onClick={(e) => { e.stopPropagation(); onToggle(); }} className="shrink-0 flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground" data-testid={`orgnode-toggle-${emp.id}`}>
          {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}{kidCount}
        </button>
      )}
    </div>
  );
}

/* ───────── Table view ───────── */
function TableView({ emps, allEmps, canManage, onAssign }) {
  const managerOptions = useMemo(() => allEmps.filter((e) => e.user_id), [allEmps]);
  return (
    <div className="border overflow-auto" data-testid="orgchart-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Name</th><th className="px-3 py-2">Designation</th>
            <th className="px-3 py-2">Department</th><th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Reports to</th><th className="px-3 py-2 text-right">Direct reports</th>
          </tr>
        </thead>
        <tbody>
          {emps.map((e) => (
            <tr key={e.id} className="border-t hover:bg-muted/20" data-testid={`orgtable-row-${e.id}`}>
              <td className="px-3 py-2 font-medium">
                {e.user_id ? <PersonChip userId={e.user_id} name={e.full_name} /> : e.full_name}
              </td>
              <td className="px-3 py-2 text-muted-foreground">{e.designation || "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{e.department || "—"}</td>
              <td className="px-3 py-2 text-xs">{(e.employment_type || "—").replace(/_/g, " ")}</td>
              <td className="px-3 py-2">
                {canManage ? (
                  <Select value={e.reporting_manager_id || NONE}
                    onValueChange={(v) => onAssign(e.id, v === NONE ? null : v)}>
                    <SelectTrigger className="h-8 w-48 rounded-none text-xs" data-testid={`orgtable-mgr-select-${e.id}`}>
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— No manager —</SelectItem>
                      {managerOptions.filter((m) => m.user_id !== e.user_id).map((m) => (
                        <SelectItem key={m.user_id} value={m.user_id}>{m.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (e.manager_name || "—")}
              </td>
              <td className="px-3 py-2 text-right font-mono">{e.direct_reports || 0}</td>
            </tr>
          ))}
          {emps.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No employees match the filter.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ───────── AI Suggestions ───────── */
function AiSuggestions({ wsId, canManage, onAssign }) {
  const [scope, setScope] = useState("unassigned");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(() => new Set());
  const [applyingAll, setApplyingAll] = useState(false);

  const generate = async () => {
    setBusy(true); setResult(null); setApplied(new Set());
    try {
      const { data: job } = await api.post(`/workspaces/${wsId}/org-chart/ai-suggest`, { scope });
      const deadline = Date.now() + 180000;
      while (Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 3000));
        // eslint-disable-next-line no-await-in-loop
        const { data: st } = await api.get(`/workspaces/${wsId}/org-chart/ai-suggest/${job.job_id}`);
        if (st.status === "done") {
          setResult(st);
          if (!st.suggestions?.length) toast.info(st.note || "No suggestions generated");
          return;
        }
        if (st.status === "failed") { toast.error(st.error || "AI suggestion failed"); return; }
      }
      toast.error("AI suggestion timed out — please try again");
    } catch (e) { toast.error(apiErrorMessage(e, "AI suggestion failed")); }
    finally { setBusy(false); }
  };

  const apply = async (s) => {
    const ok = await onAssign(s.employee_id, s.manager_user_id);
    if (ok) setApplied((a) => new Set([...a, s.employee_id]));
  };

  const applyAll = async () => {
    setApplyingAll(true);
    for (const s of result.suggestions.filter((x) => !applied.has(x.employee_id))) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await onAssign(s.employee_id, s.manager_user_id);
      if (ok) setApplied((a) => new Set([...a, s.employee_id]));
    }
    setApplyingAll(false);
  };

  const CONF = { high: "bg-emerald-500/15 text-emerald-600", medium: "bg-amber-500/15 text-amber-600", low: "bg-red-500/15 text-red-600" };
  return (
    <div className="space-y-4" data-testid="orgchart-ai-panel">
      <div className="border border-violet-500/30 bg-violet-500/5 p-4 flex flex-wrap items-center gap-3">
        <Sparkles className="w-5 h-5 text-violet-600" />
        <div className="flex-1 min-w-48">
          <p className="text-sm font-semibold">AI-suggested reporting lines</p>
          <p className="text-xs text-muted-foreground">Claude analyzes designations & departments and proposes who should report to whom. Nothing changes until you apply.</p>
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-56 rounded-none" data-testid="ai-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Only unassigned employees</SelectItem>
            <SelectItem value="all">Re-evaluate everyone</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={generate} disabled={busy || !canManage} className="rounded-none" data-testid="ai-generate-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
          {busy ? "Analyzing…" : "Generate suggestions"}
        </Button>
      </div>
      {!canManage && <p className="text-xs text-muted-foreground">You need employee-management permission to generate/apply suggestions.</p>}
      {result?.suggestions?.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{result.suggestions.length} suggestion(s) · {applied.size} applied</p>
            <Button size="sm" variant="outline" className="rounded-none" onClick={applyAll}
              disabled={applyingAll || applied.size === result.suggestions.length} data-testid="ai-apply-all-btn">
              {applyingAll ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}Apply all
            </Button>
          </div>
          <div className="border divide-y">
            {result.suggestions.map((s) => (
              <div key={s.employee_id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`ai-suggestion-${s.employee_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm"><b>{s.employee_name}</b> <span className="text-muted-foreground text-xs">({s.employee_designation || "?"})</span>
                    <span className="mx-1.5 text-muted-foreground">→</span>
                    <b>{s.manager_name}</b> <span className="text-muted-foreground text-xs">({s.manager_designation || "?"})</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.reason}{s.current_manager ? ` · currently reports to ${s.current_manager}` : ""}</p>
                </div>
                <Badge variant="outline" className={`rounded-none border-0 text-[10px] ${CONF[s.confidence]}`}>{s.confidence}</Badge>
                {applied.has(s.employee_id)
                  ? <Badge className="rounded-none bg-emerald-600">Applied</Badge>
                  : <Button size="sm" className="rounded-none" onClick={() => apply(s)} data-testid={`ai-apply-${s.employee_id}`}><Check className="w-4 h-4 mr-1" />Apply</Button>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
